import { useMemo } from "react";
import { SearchableSelect } from "./SearchableSelect";
import { useGeoDirectory } from "../context/GeoDirectoryContext";
import { toDisplayCamelCase } from "../utils/textCase";
import {
  districtSelectOptionsFromStore,
  mandalSelectOptionsFromStore,
  villageSelectOptionsFromStore,
} from "../utils/geoDirectorySelectOptions";

export type GeoLocationFiltersProps = {
  district: string;
  mandal: string;
  village: string;
  onDistrictChange: (district: string) => void;
  onMandalChange: (mandal: string) => void;
  onVillageChange: (village: string) => void;
  /** Prefix for input ids (e.g. `app-geo`, `sub-ben-geo`). */
  idPrefix: string;
  /**
   * When true, village option values are `"{id}\\t{name}"` so stable geo ids can be parsed (e.g. Agent master).
   * Leave false for filters that only match on village name.
   */
  embedVillageDbIdInValue?: boolean;
  /**
   * `filter`: optional “All …” rows (lists / reports).
   * `applicant`: required picks from directory; current mandal/village kept if not in the tree.
   */
  purpose?: "filter" | "applicant";
};

export function GeoLocationFilters({
  district,
  mandal,
  village,
  onDistrictChange,
  onMandalChange,
  onVillageChange,
  idPrefix,
  embedVillageDbIdInValue = false,
  purpose = "filter",
}: GeoLocationFiltersProps) {
  const { store, loading: geoLoading } = useGeoDirectory();
  const geoReady = Boolean(store) && !geoLoading;

  const districtOptions = useMemo(() => {
    const fromDir = districtSelectOptionsFromStore(store);
    const inDir = fromDir.some((o) => o.value === district);
    const prefix =
      purpose === "applicant" && district && !inDir
        ? [{ value: district, label: toDisplayCamelCase(district), rowKey: "current-district" }]
        : [];
    if (purpose === "applicant") return [...prefix, ...fromDir];
    return [{ value: "", label: "All districts", rowKey: "all-districts" }, ...fromDir];
  }, [store, purpose, district]);

  const mandalOptions = useMemo(() => {
    const fromDir = mandalSelectOptionsFromStore(store, district);
    const inDir = fromDir.some((o) => o.value === mandal);
    const prefix =
      purpose === "applicant" && mandal && !inDir ? [{ value: mandal, label: toDisplayCamelCase(mandal), rowKey: "current-mandal" }] : [];
    if (purpose === "applicant") return [...prefix, ...fromDir];
    return [{ value: "", label: "All mandals", rowKey: "all-mandals" }, ...fromDir];
  }, [store, district, mandal, purpose]);

  const villageOptions = useMemo(() => {
    const fromDir = villageSelectOptionsFromStore(store, district, mandal, embedVillageDbIdInValue);
    const inDir = fromDir.some((o) => o.value === village);
    const prefix =
      purpose === "applicant" && village && !inDir ? [{ value: village, label: toDisplayCamelCase(village), rowKey: "current-village" }] : [];
    if (purpose === "applicant") return [...prefix, ...fromDir];
    return [{ value: "", label: "All villages", rowKey: "all-villages" }, ...fromDir];
  }, [store, district, mandal, village, embedVillageDbIdInValue, purpose]);

  const districtPlaceholder = !geoReady ? "Loading…" : purpose === "applicant" ? "Select district" : "All districts";
  const mandalPlaceholder = !geoReady ? "Loading…" : purpose === "applicant" ? "Select mandal" : "All mandals";
  const villagePlaceholder = !geoReady ? "Loading…" : purpose === "applicant" ? "Select village" : "All villages";

  return (
    <>
      <div className="field table-filters__field">
        <label htmlFor={`${idPrefix}-district`}>District</label>
        <SearchableSelect
          id={`${idPrefix}-district`}
          value={district}
          onChange={(v) => {
            if (v === "" && purpose === "filter") {
              onDistrictChange("");
              return;
            }
            if (v !== "") onDistrictChange(v);
          }}
          options={districtOptions}
          disabled={!geoReady}
          placeholder={districtPlaceholder}
        />
      </div>
      <div className="field table-filters__field">
        <label htmlFor={`${idPrefix}-mandal`}>Mandal</label>
        <SearchableSelect
          id={`${idPrefix}-mandal`}
          value={mandal}
          onChange={onMandalChange}
          options={mandalOptions}
          disabled={!district || !geoReady}
          placeholder={mandalPlaceholder}
        />
      </div>
      <div className="field table-filters__field">
        <label htmlFor={`${idPrefix}-village`}>Village</label>
        <SearchableSelect
          id={`${idPrefix}-village`}
          value={village}
          onChange={onVillageChange}
          options={villageOptions}
          disabled={!district || !mandal || !geoReady}
          placeholder={villagePlaceholder}
        />
      </div>
    </>
  );
}
